import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { NAV } from '@/constants/testIds';
import { Menu, X } from 'lucide-react';
import BrandLogo from '@/components/site/BrandLogo';

const links = [
  { id: 'focus', label: 'Range', testid: 'nav-range-link', href: '/#focus' },
  { id: 'craft', label: 'The Craft', testid: NAV.craftLink, href: '/#craft' },
  { id: 'catalog', label: 'Catalogue', testid: NAV.productsLink, href: '/#catalog' },
  { id: 'recipes', label: 'Recipes', testid: 'nav-recipes-link', href: '/recipes', route: true },
  { id: 'order', label: 'Order Online', testid: 'nav-order-link', href: '/order', route: true },
  { id: 'wholesale', label: 'Wholesale', testid: 'nav-wholesale-link', href: '/wholesale', route: true, emphasise: true },
  { id: 'export', label: 'Export', testid: 'nav-export-link', href: '/export', route: true },
  { id: 'account', label: 'Account', testid: 'nav-account-link', href: '/account', route: true },
  { id: 'contact', label: 'Contact', testid: NAV.contactLink, href: '/#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-black/80 border-b border-white/10' : 'bg-[var(--mw-bg)]/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 h-24 sm:h-28 flex items-center">
        <div className="flex-1 flex items-center">
          <Link
            to="/"
            data-testid={NAV.brand}
            aria-label="The Meat Works · Home"
            className="inline-flex items-center"
            onClick={() => setOpen(false)}
          >
            <BrandLogo size="md" asLink={false} testId="brand-logo-nav" priority />
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => {
            const emphCls = l.emphasise
              ? 'text-[var(--mw-primary)] hover:text-[var(--mw-primary-hover)] font-medium'
              : 'text-white/70 hover:text-white';
            const cls = `text-xs uppercase tracking-[0.22em] transition-colors ${emphCls}`;
            return l.route ? (
              <Link
                key={l.id}
                to={l.href}
                data-testid={l.testid}
                className={cls}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.id}
                href={l.href}
                data-testid={l.testid}
                className={cls}
              >
                {l.label}
              </a>
            );
          })}
        </nav>

        <div className="flex-1 flex items-center justify-end">
          <a
            href="/#contact"
            data-testid={NAV.quoteCta}
            className="hidden md:inline-flex text-xs uppercase tracking-[0.22em] bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-white px-5 py-3 transition-colors"
          >
            Request a Quote
          </a>

          <button
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-white p-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) =>
              l.route ? (
                <Link
                  key={l.id}
                  to={l.href}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => setOpen(false)}
                  className="text-sm uppercase tracking-[0.22em] text-white/80"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.id}
                  href={l.href}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => setOpen(false)}
                  className="text-sm uppercase tracking-[0.22em] text-white/80"
                >
                  {l.label}
                </a>
              )
            )}
            <a
              href="/#contact"
              data-testid={`${NAV.quoteCta}-mobile`}
              onClick={() => setOpen(false)}
              className="text-sm uppercase tracking-[0.22em] bg-[var(--mw-primary)] text-white px-5 py-3 text-center"
            >
              Request a Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
